import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface AudioWaveformProps {
  analyserNode: AnalyserNode | null;
  isRecording: boolean;
  className?: string;
}

export function AudioWaveform({
  analyserNode,
  isRecording,
  className,
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    if (!analyserNode || !isRecording || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size with device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording) return;

      animationRef.current = requestAnimationFrame(draw);
      analyserNode.getByteFrequencyData(dataArray);

      // Clear canvas
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Draw bars - use fewer bars for cleaner look
      const barCount = 32;
      const barWidth = rect.width / barCount - 2;
      const step = Math.floor(bufferLength / barCount);

      for (let i = 0; i < barCount; i++) {
        const dataIndex = i * step;
        const value = dataArray[dataIndex] || 0;
        const barHeight = Math.max(2, (value / 255) * rect.height * 0.9);

        // Calculate x position (centered bars)
        const x = i * (barWidth + 2) + 1;
        const y = (rect.height - barHeight) / 2;

        // Gradient color from blue to purple based on bar index
        const hue = 220 + (i / barCount) * 40;
        ctx.fillStyle = `hsl(${hue}, 70%, 55%)`;

        // Draw rounded bar
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, 2);
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyserNode, isRecording]);

  return (
    <canvas
      ref={canvasRef}
      className={cn("h-10 w-full", className)}
      style={{ width: "100%", height: "40px" }}
    />
  );
}
